import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { IconButton, Menu, MenuItem, Tooltip, ListItemIcon, ListItemText, Badge, TextField, InputAdornment, Box, Typography, Divider } from '@mui/material';
import Icon from '@mui/material/Icon';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

import DialogOrganisatieActivate from '../../controls/dialogs/DialogOrganisatieActivate';

import { MijnOrganisatieListItem } from '../../../models/user/mijnOrganisatieListItem';
import { userManagementService } from '../../../services/user-management.service';
import { authService } from '../../../services/auth.service';

// Categorisatie op basis van de prefix van het organisatie-id (deel voor de eerste '-')
const ORGANISATIE_CATEGORIES: { key: string; label: string; prefixes: string[] }[] = [
    { key: 'netbeheerder', label: 'Netbeheerders', prefixes: ['netbeheerder'] },
    { key: 'gemeente', label: 'Gemeentes', prefixes: ['gemeente'] },
    { key: 'provincie', label: 'Provincies', prefixes: ['provincie'] },
    { key: 'woco', label: 'Woningcorporaties', prefixes: ['woco'] },
    { key: 'waterschap', label: 'Waterschappen', prefixes: ['waterschap'] },
];
const OVERIGE_CATEGORY = { key: 'overige', label: 'Overige' };

const getCategoryKey = (id: string): string => {
    const prefix = (id.split('-')[0] ?? '').toLowerCase();
    const match = ORGANISATIE_CATEGORIES.find((category) => category.prefixes.includes(prefix));
    return match ? match.key : OVERIGE_CATEGORY.key;
};


const MijnOrganisatiesNavMenu: React.FC = () => {
    const navigate = useNavigate();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [mijnOrganisaties, setMijnOrganisaties] = useState<MijnOrganisatieListItem[]>([]);
    const [selectedOrganisatie, setSelectedOrganisatie] = useState<MijnOrganisatieListItem>();
    const [mijnOrganisatie, setMijnOrganisatie] = useState<MijnOrganisatieListItem>();
    const [open, setOpenDialog] = useState(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState<string>('');

    const openMenu = Boolean(anchorEl);
    const hasMijnOrganisatieData = useRef(false);

    const filteredGroups = useMemo(() => {
        const term = search.trim().toLowerCase();
        const filtered = mijnOrganisaties.filter(
            (organisatie) => !term || organisatie.displayName.toLowerCase().includes(term)
        );

        return [...ORGANISATIE_CATEGORIES, OVERIGE_CATEGORY]
            .map((category) => ({
                key: category.key,
                label: category.label,
                items: filtered
                    .filter((organisatie) => getCategoryKey(organisatie.id) === category.key)
                    .sort((a, b) => a.displayName.localeCompare(b.displayName)),
            }))
            .filter((group) => group.items.length > 0);
    }, [mijnOrganisaties, search]);

    const filteredCount = useMemo(
        () => filteredGroups.reduce((total, group) => total + group.items.length, 0),
        [filteredGroups]
    );

    useEffect(() => {
        getStoredOrganisatie();

        // Ensure the fetch happens only once
        if (!hasMijnOrganisatieData.current) {
            fetchMijnOrganisaties();
            hasMijnOrganisatieData.current = true; // Mark as fetched
        }
    }, []); // Empty dependency array ensures this runs once when the component mounts

    const getStoredOrganisatie = async () => {
        const organisatie = userManagementService.getStoredOrganisatie();
        if (organisatie) {
            setMijnOrganisatie(organisatie);
        }
    };

    const fetchMijnOrganisaties = async () => {
        setLoading(true);
        try {
            const items = await userManagementService.mijnOrganisaties();
            if (items) {
                if (items.length === 1 && !mijnOrganisatie) {
                    const storedOrganisatie = userManagementService.getStoredOrganisatie();
                    if (!storedOrganisatie) {
                        const organisatie = items[0] as MijnOrganisatieListItem;
                        setSelectedOrganisatie(organisatie);
                        userManagementService.setStoredOrganisatie(organisatie);
                        setMijnOrganisatie(organisatie);
                        window.location.reload(); // Forces a full page reload
                    }
                    else {
                        setMijnOrganisatie(storedOrganisatie);
                    }
                }
                const sortedItems = [...items].sort((a, b) => a.displayName.localeCompare(b.displayName));
                setMijnOrganisaties(sortedItems); // Assuming the API response is a JSON object containing array of items
            }

            setLoading(false);
        }
        catch (error: any) {
            // If token refresh failed, redirect to the logout page
            if (error.message === 'Unable to refresh token') {
                navigate('/verlopen');
            }
            setError(error.message || 'Er is een fout opgetreden');
            setLoading(false);
        }
    };

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setSearch('');
    };

    const handleSelectOrganisatie = (organisatie: MijnOrganisatieListItem) => {
        setSelectedOrganisatie(organisatie)
        openDialog();
    };

    const openDialog = () => {
        setOpenDialog(true);
        handleCloseMenu();
    };

    const closeDialog = () => {
        setOpenDialog(false);
    };

    const handleConfirmSimulation = () => {
        userManagementService.setStoredOrganisatie(selectedOrganisatie);
        closeDialog();
        setMijnOrganisatie(selectedOrganisatie);
        // navigate(0);
        window.location.reload(); // window.location.replace('/plannen'); // Forces a full page reload
    }

    const onClickNavigate = (path: string) => {
        if (path.startsWith('http')) {
            window.open(path, '_blank', 'noopener,noreferrer');
        } 
        else {
            navigate(path);
        }
        handleCloseMenu();
    };

    return (
        <div>
            {mijnOrganisaties && mijnOrganisaties.length > 1 && (
                <>
                    <Tooltip title={mijnOrganisatie
                        ? <>
                                Actieve organisatie: {mijnOrganisatie.displayName}.
                                <br />
                                Kies een andere organisatie als je een andere organisatie wilt gebruiken.
                            </>
                        : "Kies welke organisatie je wilt gebruiken"
                    }>
                        <IconButton
                            onClick={handleMenuClick}
                            className="btn-nav-header btn-organisatie-menu"
                            size="large"
                            sx={{
                                ml: 2,
                                ...(mijnOrganisatie && {
                                    // backgroundColor: 'rgba(124, 91, 174, 0.05) !important',
                                    // '&:hover': {
                                    //     backgroundColor: 'rgba(124, 91, 174, 0.2) !important',
                                    // },

                                    backgroundColor: 'rgba(46, 125, 66, 0.15) !important',
                                    '&:hover': {
                                        backgroundColor: 'rgba(46, 125, 66, 0.3) !important',
                                    },
                                }),
                            }}
                            aria-haspopup="true">
                            {mijnOrganisatie ? (
                                <Badge
                                    variant="dot"
                                    overlap="circular"
                                    anchorOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            backgroundColor: 'rgb(46, 125, 66);',
                                            color: 'rgb(46, 125, 66);',
                                            top: -4,
                                            right: -4,
                                        }
                                    }}
                                    >
                                    <Icon>business</Icon>
                                </Badge>
                            ) : (
                                <Icon>business</Icon>
                            )}
                        </IconButton>
                    </Tooltip>
                    <Menu
                        id="organisatie-menu"
                        className="navigation nav-menu"
                        anchorEl={anchorEl}
                        open={openMenu}
                        onClose={handleCloseMenu}
                        slotProps={{
                            paper: {
                                elevation: 0,
                                sx: {
                                width: 300,
                                overflow: 'visible',
                                // NB: bewust GEEN `filter: drop-shadow(...)`. Safari hertekent de
                                // drop-shadow-regio niet als de menu-hoogte krimpt (bv. bij filteren),
                                // waardoor een "ghost" schaduw-rechthoek blijft hangen. box-shadow is
                                // aan de layout-box gebonden en heeft dit probleem niet.
                                boxShadow: '0px 2px 8px rgba(0,0,0,0.32)',
                                mt: 1.5,
                                '& .MuiAvatar-root': {
                                    width: 32,
                                    height: 32,
                                    ml: -0.5,
                                    mr: 1,
                                },
                                '&::before': {
                                    content: '""',
                                    display: 'block',
                                    position: 'absolute',
                                    top: 0,
                                    right: 14,
                                    width: 10,
                                    height: 10,
                                    bgcolor: 'background.paper',
                                    transform: 'translateY(-50%) rotate(45deg)',
                                    zIndex: 0,
                                },
                                },
                            },
                        }}
                        MenuListProps={{
                            sx: {
                                pt: 0,
                                maxHeight: 630,
                                overflowY: 'auto',
                                width: 300,
                            },
                        }}
                        disableAutoFocusItem
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
                        <Box
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 3,
                                backgroundColor: 'background.paper',
                                px: 1.5,
                                pt: 1,
                                pb: 0.5,
                            }}>
                            <Typography variant="subtitle1" fontWeight={600} mb={1}>
                                Organisaties
                            </Typography>

                            <TextField
                                fullWidth
                                variant="filled"
                                size="small"
                                hiddenLabel
                                autoFocus
                                placeholder="Zoek organisatie..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => e.stopPropagation()}
                                sx={{
                                    '& .MuiFilledInput-root': {
                                        borderRadius: '4px',
                                        backgroundColor: '#f5f5f5',
                                    },
                                }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: search ? (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    aria-label="wis zoekfilter"
                                                    onClick={() => setSearch('')}>
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        ) : undefined,
                                    },
                                }}
                            />

                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                {search.trim().length > 0
                                    ? `${filteredCount} van ${mijnOrganisaties.length} organisaties`
                                    : filteredCount === 1
                                        ? '1 organisatie'
                                        : `${filteredCount} organisaties`}
                            </Typography>

                            <Divider sx={{ mt: 0.75 }} />
                        </Box>

                        {filteredGroups.length === 0 && (
                            <MenuItem disabled>Geen organisaties gevonden</MenuItem>
                        )}

                        {filteredGroups.map((group, groupIndex) => (
                            <Box key={group.key}>
                                {groupIndex > 0 && <Divider sx={{ my: 0.5, mx: 1.5 }} />}
                                <Typography
                                    variant="overline"
                                    color="text.secondary"
                                    sx={{ px: 2, pt: 0.5, display: 'block' }}>
                                    {group.label}
                                </Typography>

                                {group.items.map((organisatie) =>
                                    (mijnOrganisatie && mijnOrganisatie.id === organisatie.id) ? (
                                        <Tooltip title={`${mijnOrganisatie.displayName} beheren`} key={organisatie.id}>
                                            <MenuItem
                                                style={{ backgroundColor: '#d4edda', color: '#2E7D42' }}
                                                onClick={() => onClickNavigate(`/mijn/organisatie/${organisatie.id}`)}>
                                                <ListItemIcon sx={{ minWidth: 36, transform: 'translateX(-7px)' }}>
                                                    <span className="afstemmings-status-icon afgestemd active-organization-menu-icon">
                                                        <span className="material-icons material-symbols-rounded">check</span>
                                                    </span>
                                                </ListItemIcon>
                                                <ListItemText>{organisatie.displayName}</ListItemText>
                                            </MenuItem>
                                        </Tooltip>
                                    ) : (
                                        <MenuItem key={organisatie.id} onClick={() => handleSelectOrganisatie(organisatie)}>
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <Box sx={{ width: 24, height: 24 }} />
                                            </ListItemIcon>
                                            <ListItemText>{organisatie.displayName}</ListItemText>
                                        </MenuItem>
                                    )
                                )}
                            </Box>
                        ))}
                    </Menu>

                    {selectedOrganisatie && selectedOrganisatie.displayName && (
                        <DialogOrganisatieActivate
                            open={open}
                            organisatieNaam={selectedOrganisatie.displayName.toString()}
                            onClose={() => closeDialog()}
                            onConfirm={handleConfirmSimulation}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default MijnOrganisatiesNavMenu;
